/**
 * Minimal, display-only JWT payload decoder.
 *
 * This does NOT verify the signature — it only base64url-decodes the payload so
 * the UI can read non-sensitive claims like `exp` (to warn before expiry). Never
 * trust these values for a security decision; the server verifies the signature.
 */

export interface DecodedTokenPayload {
  /** Expiry, in SECONDS since epoch (standard JWT `exp`). */
  exp?: number;
  /** Issued-at, in seconds since epoch. */
  iat?: number;
  sub?: string;
  role?: string;
  aud?: string;
  username?: string;
  [claim: string]: unknown;
}

/**
 * Decode a JWT's payload. Returns null for anything malformed (not three parts,
 * bad base64, non-JSON) so callers can treat "can't read it" the same as
 * "no token".
 */
export function decodeToken(
  token: string | null | undefined,
): DecodedTokenPayload | null {
  if (!token) return null;

  const parts = token.split('.');
  if (parts.length !== 3) return null;

  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json =
      typeof atob === 'function'
        ? decodeURIComponent(
            atob(payload)
              .split('')
              .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join(''),
          )
        : Buffer.from(payload, 'base64').toString('utf-8');
    const parsed = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as DecodedTokenPayload;
  } catch {
    return null;
  }
}

/** Milliseconds until the token expires. Null if no/invalid token or no `exp`. */
export function msUntilExpiry(
  token: string | null | undefined,
  now: number = Date.now(),
): number | null {
  const decoded = decodeToken(token);
  if (!decoded || typeof decoded.exp !== 'number') return null;
  return decoded.exp * 1000 - now;
}

/**
 * True when the token is still valid but expires within `withinMs` (default 24h).
 * False when there's no token, it's already expired, or expiry is further out.
 */
export function isExpiringSoon(
  token: string | null | undefined,
  withinMs: number = 24 * 60 * 60 * 1000,
  now: number = Date.now(),
): boolean {
  const ms = msUntilExpiry(token, now);
  if (ms === null) return false;
  return ms > 0 && ms <= withinMs;
}
